'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { ScraperRunReport, ScraperSchedule, SiteCredential } from '@/lib/scraper/types';
import type { AuctionItem } from '@/app/components/AuctionDetailModal';
import { scraperApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const KEYS = {
  reports: 'metricaleiloes-scraper-reports',
  schedule: 'metricaleiloes-scraper-schedule',
  credentials: 'metricaleiloes-scraper-credentials',
  results: 'metricaleiloes-scraper-results',
};

export type ScraperRunStatus = 'idle' | 'running' | 'success' | 'error';

interface SiteConfigPublic {
  id: string;
  name: string;
  url: string;
  enabled: boolean;
}

interface ScraperContextValue {
  status: ScraperRunStatus;
  isRunning: boolean;
  lastReport: ScraperRunReport | null;
  reports: ScraperRunReport[];
  scrapedAuctions: AuctionItem[];
  schedule: ScraperSchedule;
  credentials: SiteCredential[];
  runNow: (sites: SiteConfigPublic[]) => Promise<void>;
  updateSchedule: (s: Partial<ScraperSchedule>) => void;
  setCredential: (siteId: string, username: string, password: string) => void;
  removeCredential: (siteId: string) => void;
  clearHistory: () => void;
}

const ScraperContext = createContext<ScraperContextValue | null>(null);

function load<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
}

function save(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* noop */
  }
}

const DEFAULT_SCHEDULE: ScraperSchedule = { enabled: false, intervalHours: 6 };

export function ScraperProvider({ children }: { children: React.ReactNode }) {
  const { isOnline } = useAuth();
  const [status, setStatus] = useState<ScraperRunStatus>('idle');
  const [reports, setReports] = useState<ScraperRunReport[]>([]);
  const [scrapedAuctions, setScrapedAuctions] = useState<AuctionItem[]>([]);
  const [schedule, setScheduleState] = useState<ScraperSchedule>(DEFAULT_SCHEDULE);
  const [credentials, setCredentialsState] = useState<SiteCredential[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  /* Carregar do localStorage */
  useEffect(() => {
    setReports(load(KEYS.reports, []));
    setScrapedAuctions(load(KEYS.results, []));
    setScheduleState(load(KEYS.schedule, DEFAULT_SCHEDULE));
    setCredentialsState(load(KEYS.credentials, []));
  }, []);

  const lastReport = reports[0] ?? null;

  /* Agendamento automático */
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!schedule.enabled) return;

    const check = () => {
      void (async () => {
        if (!schedule.lastRun) return;
        const last = new Date(schedule.lastRun).getTime();
        const due = last + schedule.intervalHours * 3600000;
        if (Date.now() < due) return;
        try {
          const all = await scraperApi.getSites();
          const enabled = all
            .filter((s) => s.enabled)
            .map((s) => ({ id: s.id, name: s.name, url: s.url, enabled: s.enabled }));
          if (enabled.length === 0) return;
          await runNow(enabled);
        } catch {
          console.warn(
            '[ScraperContext] não foi possível obter sites do servidor para o agendamento'
          );
        }
      })();
    };

    timerRef.current = setInterval(check, 60000); // verifica a cada 1 min
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule]);

  const runNow = useCallback(
    async (sites: SiteConfigPublic[]) => {
      if (status === 'running') return;

      if (!isOnline) {
        console.warn('[ScraperContext] BE indisponível — não é possível executar o scraper');
        setStatus('error');
        return;
      }

      setStatus('running');

      try {
        // Sempre usa o BE — o scraper roda no servidor, não no browser
        const beReport = await scraperApi.run(
          sites.map((s) => ({ id: s.id, name: s.name, url: s.url, enabled: s.enabled })),
          'manual'
        );

        // Adapta o relatório do BE para o formato interno
        const report: ScraperRunReport = {
          id: beReport.id,
          startedAt: beReport.startedAt,
          finishedAt: beReport.finishedAt,
          totalFound: beReport.totalFound,
          totalNew: beReport.totalFound,
          triggeredBy: 'manual',
          sites: beReport.sites.map((s) => ({
            siteId: s.siteId,
            siteName: s.siteName,
            status: s.status,
            count: s.count,
            auctions: [], // dados já foram persistidos no banco pelo BE
            error: s.error,
            duration: s.duration,
            timestamp: beReport.finishedAt,
          })),
        };

        const newReports = [report, ...reports].slice(0, 20);
        setReports(newReports);
        save(KEYS.reports, newReports);

        // Auctions vêm do banco (página Oportunidades / tabela) — sem necessidade de manter em contexto
        setScrapedAuctions([]);
        save(KEYS.results, []);

        const updatedSchedule = { ...schedule, lastRun: new Date().toISOString() };
        if (schedule.enabled) {
          updatedSchedule.nextRun = new Date(
            Date.now() + schedule.intervalHours * 3600000
          ).toISOString();
        }
        setScheduleState(updatedSchedule);
        save(KEYS.schedule, updatedSchedule);

        setStatus(report.sites.some((s) => s.status === 'error') ? 'error' : 'success');
      } catch (err) {
        console.error('[ScraperContext] runNow error:', err);
        setStatus('error');
      }
    },
    [status, isOnline, reports, schedule]
  );

  const updateSchedule = useCallback(
    (updates: Partial<ScraperSchedule>) => {
      const next = { ...schedule, ...updates };
      setScheduleState(next);
      save(KEYS.schedule, next);
    },
    [schedule]
  );

  const setCredential = useCallback((siteId: string, username: string, password: string) => {
    setCredentialsState((prev) => {
      const filtered = prev.filter((c) => c.siteId !== siteId);
      const next = [...filtered, { siteId, username, password }];
      save(KEYS.credentials, next);
      return next;
    });
  }, []);

  const removeCredential = useCallback((siteId: string) => {
    setCredentialsState((prev) => {
      const next = prev.filter((c) => c.siteId !== siteId);
      save(KEYS.credentials, next);
      return next;
    });
  }, []);

  const clearHistory = useCallback(() => {
    setReports([]);
    setScrapedAuctions([]);
    save(KEYS.reports, []);
    save(KEYS.results, []);
  }, []);

  return (
    <ScraperContext.Provider
      value={{
        status,
        isRunning: status === 'running',
        lastReport,
        reports,
        scrapedAuctions,
        schedule,
        credentials,
        runNow,
        updateSchedule,
        setCredential,
        removeCredential,
        clearHistory,
      }}
    >
      {children}
    </ScraperContext.Provider>
  );
}

export function useScraper() {
  const ctx = useContext(ScraperContext);
  if (!ctx) throw new Error('useScraper must be used inside ScraperProvider');
  return ctx;
}
